import { useMutation } from "@apollo/client/react";
import { useMemo } from "react";

import { useCurrentUser } from "./use-current-user";
import SET_ACCOUNT_PREFERENCE from "../graphql/mutations/SetAccountPreference";
import { getPreferenceValue } from "../utils/preferences";

export const COMPANION_TOOLTIPS_DISABLED_KEY =
  "disable-companion-tooltips";

const parseDisabled = (preferences: unknown): boolean =>
  Boolean(
    getPreferenceValue(preferences, COMPANION_TOOLTIPS_DISABLED_KEY),
  );

export function useCompanionTooltipsDisabled(): boolean {
  const { user } = useCurrentUser();
  const preferences = user?.account?.preferences;
  return useMemo(() => parseDisabled(preferences), [preferences]);
}

export function useSetCompanionTooltipsDisabled() {
  const { user, refetch, loading: userLoading } = useCurrentUser();
  const [mutate, { loading: mutationLoading }] = useMutation(
    SET_ACCOUNT_PREFERENCE,
  );
  const currentValue = parseDisabled(user?.account?.preferences);
  const loading = userLoading || mutationLoading;

  const setDisabled = async (value: boolean): Promise<boolean> => {
    if (value === currentValue || loading) return false;
    const result = await mutate({
      variables: {
        key: COMPANION_TOOLTIPS_DISABLED_KEY,
        value,
      },
    });
    if (!result.data?.setAccountPreference?.ok) {
      throw new Error("Failed to save account preference");
    }
    await refetch();
    return true;
  };

  return { setDisabled, loading };
}
